import Modal from 'components/Modal';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import { TYPE_BLOCK_RADIO } from '../BlockHourType';
import { Avatar, Form } from 'antd';
import moment from 'moment';
import StaffSearchInput from 'widgets/Staff/StaffSearchInput';
import { IStaffItemData } from 'services/shop/types/staff';
import { getMessageError, IResponseDataBody } from 'services/response';
import appointmentApis from 'features/appointment/services/apis';
import { ILockTimeItem } from 'features/appointment/services/types/api';
import { ColumnsType } from 'antd/es/table';
import Box from 'components/Box';
import Text from 'components/Text';
import Button from 'components/Button';
import Table from 'components/Table';
import { toast } from 'react-toastify';
import { verifyAdminPW } from 'widgets/RequestAdminLocalPW/Callback';
import DatePickerByTypeView from 'components/DateViewControls/DatePickerByTypeView';
import { CALENDAR_VIEW_TYPE } from 'features/appointment/services/constants';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hooks';
import settingStaffActions from 'features/settingStaff/services/actions';

export type ViewHistoryModalRef = {
  open: () => void;
};

type ViewHistoryModalProps = {};

interface IFormValues {
  blockHourType: TYPE_BLOCK_RADIO;
  staffId: string;
  timePeriod: {
    startTime: dayjs.Dayjs;
    endTime: dayjs.Dayjs;
  };
}

enum SOURCE_TYPE {
  OFF_DAY = 'OFF',
  BLOCK_HOURS = 'BLOCK',
  ON_BREAK = 'BREAK',
}

const sourceTypeConfig: Record<SOURCE_TYPE, { label: string; bgColor: string; color: string }> = {
  [SOURCE_TYPE.OFF_DAY]: {
    label: 'Day Offs',
    bgColor: '#E5E6EB',
    color: '#1D2129',
  },
  [SOURCE_TYPE.BLOCK_HOURS]: {
    label: 'Block Hours',
    bgColor: '#ADADAD',
    color: '#FFF',
  },
  [SOURCE_TYPE.ON_BREAK]: {
    label: 'On Break',
    bgColor: '#EBA33B',
    color: '#FFF',
  },
};

const formatTimeRange = (startTime: string, endTime: string) => {
  const start = moment(startTime, 'MM-DD-YYYY HH:mm:ss').format('hh:mm A');
  const end = moment(endTime, 'MM-DD-YYYY HH:mm:ss').format('hh:mm A');

  return `${start} - ${end}`;
};

const ViewHistoryModal = forwardRef<ViewHistoryModalRef, ViewHistoryModalProps>(({}, ref) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm<IFormValues>();
  const [data, setData] = useState<ILockTimeItem[] | null>(null);
  const timePeriod = Form.useWatch('timePeriod', form);
  const staffId = Form.useWatch('staffId', form);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const fetchHistoryLockTime = async ({
    staffId,
    startTime,
    endTime,
  }: {
    staffId?: string;
    startTime: string;
    endTime: string;
  }) => {
    try {
      const res: IResponseDataBody = await appointmentApis.getLockBreakTimes({
        staffId,
        startTime,
        endTime,
      });

      if (res?.data?.data) {
        setData(res?.data?.data);
      }
    } catch (error) {}
  };

  const onClose = () => {
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      setVisible(true);
    },
  }));

  const initialValues = {
    blockHourType: TYPE_BLOCK_RADIO.BLOCK_HOURS,
    staffId: '',
    timePeriod: { startTime: dayjs(), endTime: dayjs() },
  };

  const columns: ColumnsType<ILockTimeItem> = [
    {
      title: 'Staff',
      dataIndex: 'staffName',
      key: 'staffName',
      align: 'left',
      render: (text: string, record: ILockTimeItem) =>
        text ? (
          <Box display='flex' alignItems='center' justifyContent='start' gap='2'>
            <Avatar src={record?.avatar} size={40}>
              {text?.charAt(0)?.toUpperCase() || '--'}
            </Avatar>
            <Text variant='BODY_1'>{text || '--'}</Text>
          </Box>
        ) : (
          <Text variant='BODY_1'>--</Text>
        ),
    },
    {
      title: 'Time',
      dataIndex: 'startTime',
      key: 'startTime',
      align: 'left',
      render: (text: string, record: ILockTimeItem) => formatTimeRange(text, record?.endTime),
      width: 250,
    },
    {
      title: 'Source',
      dataIndex: 'type',
      key: 'type',
      align: 'left',
      render: (text: string) => {
        const config = sourceTypeConfig[text as SOURCE_TYPE];
        return (
          <Box
            width='fit-content'
            display='flex'
            alignItems='center'
            justifyContent='center'
            borderRadius='5px'
            style={{ backgroundColor: config.bgColor, padding: '4px 8px' }}
          >
            <Text variant='BODY_1' style={{ color: config.color, fontWeight: 500 }}>
              {config?.label || '--'}
            </Text>
          </Box>
        );
      },
      width: 150,
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      align: 'left',
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_, record: ILockTimeItem) => {
        return record?.type === SOURCE_TYPE.OFF_DAY ? (
          <Button
            ntype='TERTIARY'
            height={2}
            onClick={() => {
              verifyAdminPW((_pin?: string, data?: IStaffItemData) => {
                handleNavigateToStaffDetail(record.staffId, record.shopId);
              });
            }}
          >
            Detail
          </Button>
        ) : (
          <Button
            ntype='PRIMARY'
            cardCashier
            height={2}
            onClick={() => {
              verifyAdminPW((_pin?: string, data?: IStaffItemData) => {
                handleUnlock(record.id);
              });
            }}
          >
            Unlock
          </Button>
        );
      },
    },
  ];

  useEffect(() => {
    if (visible) {
      fetchHistoryLockTime({
        startTime: timePeriod.startTime.format('MM-DD-YYYY'),
        endTime: timePeriod.endTime.format('MM-DD-YYYY'),
        staffId,
      });
    }
  }, [visible, timePeriod, staffId]);

  const handleUnlock = async (offDayStaffId: string) => {
    try {
      const res: IResponseDataBody = await appointmentApis.removeLockBreakTimes(offDayStaffId);

      if (res?.data?.data) {
        fetchHistoryLockTime({
          startTime: timePeriod.startTime.format('MM-DD-YYYY'),
          endTime: timePeriod.endTime.format('MM-DD-YYYY'),
          staffId,
        });
        toast.success('Unlock successfully');
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Unlock failed');
    }
  };

  const handleNavigateToStaffDetail = (staffId: string, shopId: string) => {
    dispatch(settingStaffActions.getSelectedStaffInfo.fetch(staffId));
    navigate(`/store/${shopId}/setting-staff/schedule`);
  };

  return (
    <Modal
      modalTitle='VIEW HISTORY'
      v2
      visible={visible}
      onClose={onClose}
      noneBodyStyle
      containerPadding={0}
      width={'50vw'}
      showClose={false}
      hiddenOkButton
    >
      <Container>
        <Form form={form} layout='vertical' initialValues={initialValues} className='form-block-hours'>
          <HeaderFilter>
            <Form.Item name='staffId' noStyle>
              <StaffSearchInput
                v2
                onPickAll={() => undefined}
                onPickStaff={(data: IStaffItemData | null) => {
                  form.setFieldValue('staffId', data?.id);
                }}
                onClearStaff={() => {
                  form.setFieldValue('staffId', '');
                }}
                showAllOption={false}
                sizeAvatar={40}
                secondary
              />
            </Form.Item>
            <Form.Item name={'timePeriod'} noStyle>
              <DatePickerByTypeView viewType={CALENDAR_VIEW_TYPE.DAY_VIEW} />
            </Form.Item>
          </HeaderFilter>
          <ScrollableTableSection>
            <TableStyled>
              <Table
                columns={columns}
                dataSource={data || []}
                rowKey='id'
                // loading={loading}
                pagination={{
                  pageSize: 100,
                  showSizeChanger: false,
                }}
              />
            </TableStyled>
          </ScrollableTableSection>
        </Form>
      </Container>
    </Modal>
  );
});

ViewHistoryModal.displayName = 'ViewHistoryModal';
export default ViewHistoryModal;

const Container = styled.div`
  width: 100%;
  .form-block-hours {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
  }
`;

const HeaderFilter = styled.div`
  display: flex;
  padding: 24px 16px 16px 16px;
  background: #fff;
  gap: 24px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);

  .ant-picker {
    width: 350px;
    .ant-picker-input {
      justify-content: flex-end;
    }
  }
`;

const ScrollableTableSection = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 24px 16px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const TableStyled = styled.div`
  flex-shrink: 0;
  .ant-table-wrapper {
    overflow: visible;
    .ant-table {
      .ant-table-container {
        .ant-table-content {
          overflow: visible;
          border: none;
          .ant-table-thead {
            overflow: hidden;
            position: sticky;
            top: 0;
            z-index: 10;

            th.ant-table-cell {
              position: sticky;
              top: 0;
              z-index: 99;
              background: #6fabb6 !important;
              border-bottom: 1px solid #ccd4dc;
              color: #fff !important;
              height: 52px;
              padding: 8px;
              font-family: Poppins;
              font-size: 14px;
              font-style: normal;
              font-weight: 600;
              line-height: normal;
              position: relative;
              &::before {
                content: none;
              }

              &:not(:last-child)::after {
                content: '';
                position: absolute;
                right: 0;
                top: 8px;
                bottom: 8px;
                width: 2px;
                background: rgba(255, 255, 255, 0.3);
                z-index: 1;
              }
            }

            tr th:first-child {
              border-top-left-radius: 8px !important;
            }

            tr th:last-child {
              border-top-right-radius: 8px !important;
            }
          }
          .ant-table-tbody {
            .ant-table-row {
              background: #ffffff;
              &:nth-child(even) {
                background: #e4f4f6;
              }
            }
          }
        }
      }
    }

    .ant-table .ant-table-thead th.ant-table-cell {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #6fabb6 !important;
      border-bottom: 1px solid #ccd4dc;
      color: #fff !important;
      height: 52px;
      padding: 8px;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      position: relative;
      &::before {
        content: none;
      }

      &:not(:last-child)::after {
        content: '';
        position: absolute;
        right: 0;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: rgba(255, 255, 255, 0.3);
      }
    }

    .ant-table .ant-table-thead tr th:first-child {
      border-top-left-radius: 8px !important;
    }

    .ant-table .ant-table-thead tr th:last-child {
      border-top-right-radius: 8px !important;
    }

    .ant-table-tbody td.ant-table-cell {
      height: auto;
      padding: 2px 8px;
      color: #131921 !important;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px;
    }

    .ant-table-tbody .ant-table-row:nth-child(even) td {
      background: #e4f4f6;
    }

    .ant-table-pagination {
      border: none;

      .ant-pagination-item.ant-pagination-item-active {
        background-color: #2d6772;
        border: none;
      }
      .ant-pagination-item.ant-pagination-item-active a {
        color: #fff;
      }
    }
  }
`;
